import React, { useState, useEffect,useCallback } from 'react';
import { Modal, Box, Typography, Button, CircularProgress, IconButton,LinearProgress, Fade, Snackbar, Alert } from '@mui/material'; // Añadido Snackbar y Alert
import { MuiOtpInput } from 'mui-one-time-password-input';
import RefreshIcon from '@mui/icons-material/Refresh';
import { grey } from '@mui/material/colors';
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined';
import BASE_URL from '../../config';

const style = {
  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
  width: 380, bgcolor: 'white', borderRadius: 4, p: 4, textAlign: 'center', outline: 'none'
};

export default function Modal2fa({ open, setOpen, data2FA, setData2FA, onSuccess }) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentQr, setCurrentQr] = useState('');
  const [timer, setTimer] = useState(30);

  // --- ESTADO PARA NOTIFICACIONES MODERNAS ---
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const showMsg = useCallback((msg, sev = 'error') => {
    setNotification({ open: true, message: msg, severity: sev });
  }, []);

  const handleCloseMsg = () => setNotification((prev) => ({ ...prev, open: false }));

  const refreshQRCode = useCallback(async () => {
    if (!data2FA.userId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/2fa/refresh-qr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: data2FA.userId }) 
      });
      const data = await res.json();
      if (res.ok && data.qrCode) {
        setCurrentQr(data.qrCode);
        if (setData2FA) setData2FA(prev => ({ ...prev, qrCode: data.qrCode }));
        setTimer(30);
        showMsg("Código QR actualizado", "success");
      } else {
        showMsg(data.message || "Error al refrescar");
      }
    } catch (e) {
      showMsg("Error de conexión");
    } finally {
      setLoading(false);
    }
  }, [data2FA.userId, setData2FA, showMsg]);

  useEffect(() => {
    if (open && data2FA.qrCode) {
      setCurrentQr(data2FA.qrCode);
      setTimer(30);
    }
  }, [open, data2FA.qrCode]);

  useEffect(() => {
    let interval = null;

    if (open) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            if (data2FA.setupRequired) {
              refreshQRCode();
            }
            return 30;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (!open) {
        setOtp('');
        setTimer(30);
      }
    };
  }, [open, data2FA.setupRequired, refreshQRCode]);

  const handleVerify = async (codeValue) => {
    const code = codeValue || otp;
    if (code.length < 6) return;
    setLoading(true);

    const endpoint = data2FA.setupRequired ? '/2fa/verify' : '/2fa/validate';
    
    try {
      const res = await fetch(`${BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: { 
          "Content-Type": "application/json",
          ...(data2FA.tempToken && { "Authorization": `Bearer ${data2FA.tempToken}` })
        },
        body: JSON.stringify({ userId: data2FA.userId, token: code }),
      });

      const data = await res.json();
      if (res.ok) {
        showMsg("Verificación exitosa", "success");
        localStorage.setItem('token', data.token);
        
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
          console.log("DEBUG: Usuario guardado desde el Modal:", data.user);
        }

        onSuccess(data.token || data2FA.tempToken);
        setOpen(false);

      } else {
        showMsg(data.message || "Código incorrecto");
        setOtp('');
      }
    } catch (e) {
      showMsg("Error al verificar código");
    } finally {
      setLoading(false);
    }
  };

  const handleResetAndSetup = async () => {
    
    setLoading(true);
    try {
        const res = await fetch(`${BASE_URL}/2fa/reset`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: data2FA.userId })
        });

        const data = await res.json();

        if (res.ok) {
            // Actualizamos el estado para que el modal "se convierta" en modo Setup
            setData2FA(prev => ({
                ...prev,
                setupRequired: true,
                qrCode: data.qrCode // El backend debe devolver el nuevo QR
            }));
            setCurrentQr(data.qrCode);
            setTimer(30);
            showMsg("Configuración reiniciada. Escanea el nuevo código.", "info");
        } else {
            showMsg(data.message || "Error al reiniciar");
        }
    } catch (e) {
        showMsg("Error de conexión al reiniciar");
    } finally {
        setLoading(false);
    }
};

  return (
    <>
      <Modal open={open} onClose={() => !loading && setOpen(false)}>
        <Fade in={open}>
          <Box sx={style}>
            <Typography variant="h6">
              {data2FA.setupRequired ? 'Verificación en dos Pasos' : 'Verificación de Seguridad'}
            </Typography>
            
            {data2FA.setupRequired && (
              <Box sx={{ mt: 2 }}>
                <IconButton disableRipple sx={{ my:1, pointerEvents: "none", color: "black", transform: "scale(1.2)", bgcolor: grey[200] }}>
                  <GppGoodOutlinedIcon/>
                </IconButton>
                <Typography variant="body2" color="textSecondary">
                  Escanea este código en Google Authenticator:
                </Typography>
                <Box sx={{ mt: 2, p: 1, border: '1px solid #eee', borderRadius: 2, display: 'inline-block', position: 'relative', bgcolor: '#fff' }}>
                  {currentQr ? (
                    <img src={currentQr} alt="QR 2FA" style={{ width: 180, height: 180, display: 'block' }} />
                  ) : (
                    <Box sx={{ width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress size={30} />
                    </Box>
                  )}
                  {loading && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                      <CircularProgress size={32} color="inherit" />
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection:'column', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                  <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <Typography variant="body2" color="textSecondary">
                      ¿El código no funciona? Refresca aquí 
                    </Typography>
                    <IconButton onClick={refreshQRCode} disabled={loading} size="small" sx={{ ml: 0.5 }}>
                      <RefreshIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Box>
                  <Box sx={{ width: '80%', mt: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(timer / 30) * 100} 
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        bgcolor: grey[200],
                        '& .MuiLinearProgress-bar': { bgcolor: timer < 10 ? '#d44a4a' : 'black' }
                      }} 
                    />
                    <Typography variant="body2" color="textSecondary" sx={{color: timer < 10 ? '#d44a4a' : 'textSecondary',my:1}}>
                      El código cambia automáticamente en {timer}s
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={{ my:1 }}>
              <Typography variant="body1" color='textSecondary' sx={{ mb:2 }}>
                Ingresa el código de 6 dígitos de tu app
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <MuiOtpInput 
                  value={otp} 
                  onChange={setOtp}
                  length={6} 
                  gap={0.8}
                  onComplete={handleVerify}
                  TextFieldsProps={{ 
                    autoFocus: true,
                    placeholder:'-',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        width: 45,
                        borderRadius: '10px',
                        bgcolor:grey[100],
                        '& fieldset': { borderColor: grey[200] },
                        '&:hover fieldset': { borderColor: grey[400] },
                        '&.Mui-focused fieldset': { borderColor: grey[600] },
                      }
                    }
                  }}
                />
              </Box>
              {!data2FA.setupRequired && (
                <Typography variant="caption" sx={{ mt: 2, display: 'block', color: grey[600] }}>
                  ¿No tienes código? {' '}
                  <span 
                    onClick={handleResetAndSetup}
                    style={{ color: '#d44a4a', cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
                  >
                    Genera uno nuevo haciendo clic aquí
                  </span>
                </Typography>
              )}
            </Box>

            <Button 
              fullWidth variant="contained" 
              sx={{ mt: 3, bgcolor: 'black', py: 1.5, borderRadius: '10px', textTransform: 'none', fontSize: '1rem', '&:hover': { bgcolor: '#333' } }} 
              onClick={() => handleVerify()} 
              disabled={loading || otp.length < 6}
            >
              {loading ? <CircularProgress size={24}/> : 'Verificar'}
            </Button>
          </Box>
        </Fade>
      </Modal>

      {/* NOTIFICACIÓN MODERNA (Snackbar) */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={4000} 
        onClose={handleCloseMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseMsg} severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}